import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSpace } from '../context/SpaceContext';
import { useSpaceConfig } from '../context/SpaceConfigContext';
import { useNavigationGuard } from '../context/NavigationGuardContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  getEntities, createEntity as createEntityApi,
  getSpacesByEntity, createSpace as createSpaceApi,
  getSpaceMembers, inviteMember, removeMember,
  syncMemberRoles, setMemberApprovalExclusion,
  getRoles, assignEntityAdmin,
  getEntityAdmins, getPendingEntityAdminInvitations, revokePendingEntityAdminInvitation,
  removeEntityAdmin, deleteEntityAdminAccount,
  removeGmsAccountant, deleteGmsAccountantAccount,
  getPendingInvitations, revokePendingInvitation,
  inviteGmsAccountant,
  getPendingGmsAccountantInvitations, revokeGmsAccountantInvitation,
  getGmsAccountants,
} from '../api/memberApi';
import DeleteMemberDialog from '../components/members/DeleteMemberDialog';
import DeleteAdminDialog  from '../components/members/DeleteAdminDialog';
import {
  getSpaceConfig, updateSpaceConfig,
  getEntityConfig, updateEntityConfig,
  getSpaceCurrencyImpact, getEntityCurrencyImpact,
  getLanguages,
  getSpaceSupportedLanguages, updateSpaceSupportedLanguages,
} from '../api/configApi';
import { getCurrencies } from '../api/itemApi';
import CurrencyChangeConfirmDialog from '../components/admin/CurrencyChangeConfirmDialog';
import {
  Box, Typography, Button, Card, Chip, Avatar, IconButton, TextField,
  Select, MenuItem, FormControl, FormHelperText, InputLabel, Divider, Table, TableBody,
  TableCell, TableHead, TableRow, Paper, Tabs, Tab, Switch, Dialog,
  DialogTitle, DialogContent, DialogActions, Tooltip, InputAdornment,
  Snackbar, Alert, Stack, Checkbox, Radio, RadioGroup, FormControlLabel,
} from '@mui/material';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// ─── Palette ─────────────────────────────────────────────────────────────────

import { C, entityColor, btnBase, ctaGreen, ctaNavy, cardBase } from '../theme/adminTokens';
function initials(name) { return (name || '?').split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase(); }
// 'space_admin' → 'Space Admin', 'space_user' → 'Space User'
function formatRole(name) { return (name || '').split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '); }

// ─── Role model ───────────────────────────────────────────────────────────────
// Global Admin   → creates entities & spaces, assigns Entity Admins
// Entity Admin   → assigned by Global Admin; manages members in their entity's spaces
// Space Admin    → assigned by Entity Admin; adds Editor/Viewer to their space only
// Editor         → content permission
// Viewer         → read-only

const ROLE_STYLE = {
  'Global Admin': { bgcolor: '#ede9fe', color: '#5b21b6' },
  'Entity Admin': { bgcolor: '#dbeafe', color: '#1d4ed8' },
  'Space Admin':  { bgcolor: '#dcfce7', color: '#15803d' },
  'Space User':   { bgcolor: '#fff7ed', color: '#c2410c' },
  'Editor':       { bgcolor: '#fff7ed', color: '#c2410c' },
  'Viewer':       { bgcolor: '#f1f5f9', color: '#475569' },
};


// Keys toggled on the Feature Flags tab — matches SpaceConfigContext.jsx's ALL_OFF.
// Used to diff spaceConfig against the last-saved snapshot without apiVersion/currencyId
// (which have their own separate Save buttons) spuriously marking this section dirty.
const FEATURE_FLAG_KEYS = [
  'payments', 'events', 'advertisers', 'aiModeration', 'selfies', 'lighting',
  'items', 'rewards', 'charities', 'subscriptions', 'recognition', 'booths',
  'hosting', 'approvals',
];
function pickFeatureFlags(cfg) {
  const out = {};
  FEATURE_FLAG_KEYS.forEach((k) => { out[k] = !!cfg?.[k]; });
  return out;
}

// ─── Shared small components ──────────────────────────────────────────────────
function RoleChip({ role }) {
  const s = ROLE_STYLE[role] ?? ROLE_STYLE['Viewer'];
  return <Chip label={role} size="small" sx={{ ...s, fontWeight: 700, fontSize: 10, letterSpacing: 0.4, height: 22, textTransform: 'uppercase' }} />;
}


function SectionHeader({ title, subtitle, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.navy, letterSpacing: -0.5 }}>{title}</Typography>
        {subtitle && <Typography variant="body2" sx={{ color: C.muted, mt: 0.5 }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: `1px solid ${C.border}` }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: C.navy }}>{label}</Typography>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)}
        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: C.green }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: C.green } }} />
    </Box>
  );
}

function InviteEntityAdminDialog({ open, entityName, onClose, onSubmit }) {
  const [email, setEmail] = React.useState('');
  const [name,  setName]  = React.useState('');
  const [busy,  setBusy]  = React.useState(false);

  const reset = () => { setEmail(''); setName(''); setBusy(false); };
  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    await onSubmit({ type: 'new', email: email.trim(), name: name.trim() });
    reset();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy, pb: 1 }}>Invite Entity Admin</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ color: C.muted, mb: 2.5 }}>
          Grant entity-level admin access to <strong>{entityName}</strong>. If the user doesn't exist yet, an invitation email will be sent.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField fullWidth size="small" label="Email address" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required
            helperText={email.trim() === '' ? 'Required' : ''}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <TextField fullWidth size="small" label="Name (optional)" value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small"
          sx={{ ...btnBase, borderColor: C.border, color: C.navy }}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" size="small" disabled={!email.trim() || busy}
          sx={{ ...btnBase, bgcolor: '#1d4ed8', color: '#fff', '&:hover': { bgcolor: '#1e40af' } }}>
          {busy ? 'Sending…' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function InviteGmsAccountantDialog({ open, onClose, onSubmit }) {
  const [email, setEmail] = React.useState('');
  const [name,  setName]  = React.useState('');
  const [busy,  setBusy]  = React.useState(false);

  const reset = () => { setEmail(''); setName(''); setBusy(false); };
  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    await onSubmit({ email: email.trim(), name: name.trim() || undefined });
    reset();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy, pb: 1 }}>Invite GMS Accountant</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ color: C.muted, mb: 2.5 }}>
          Grant cross-space financial access as a <strong>GMS Accountant</strong>. This is a system-level role — they will see all spaces across all entities. An invitation email will be sent if the user doesn't exist yet.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField fullWidth size="small" label="Email address" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required
            helperText={email.trim() === '' ? 'Required' : ''}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <TextField fullWidth size="small" label="Name (optional)" value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small"
          sx={{ ...btnBase, borderColor: C.border, color: C.navy }}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" size="small" disabled={!email.trim() || busy}
          sx={{ ...btnBase, bgcolor: '#1d4ed8', color: '#fff', '&:hover': { bgcolor: '#1e40af' } }}>
          {busy ? 'Sending…' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const BackBtn = ({ label, onClick }) => (
  <Button startIcon={<ArrowBackOutlinedIcon />} onClick={onClick} variant="outlined" size="small"
    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: C.border, color: C.navy, mb: 3, '&:hover': { borderColor: C.navy, bgcolor: 'rgba(7,33,70,0.04)' } }}>
    {label}
  </Button>
);

// btnBase / ctaGreen / ctaNavy / cardBase also live in src/theme/adminTokens.js
// (imported at the top of this file alongside the palette).

// ─── Confirm dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ open, target, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} PaperProps={{ sx: { borderRadius: 3, p: 1 } }} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>Confirm Revoke</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {target?.type === 'member'
            ? `Remove ${target?.label} from this space? They will lose access immediately.`
            : `Cancel the invitation for ${target?.label}?`}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} variant="outlined" sx={{ ...btnBase, fontWeight: 600 }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error" sx={{ ...btnBase }}>Revoke</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Unsaved Feature Flags dialog ─────────────────────────────────────────────
function UnsavedFlagsDialog({ open, saving, message, onSave, onDiscard, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} PaperProps={{ sx: { borderRadius: 3, p: 1 } }} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>Unsaved Changes</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {message ?? 'You have unsaved changes on this tab. Save them before continuing, or they will be lost.'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" disabled={saving} sx={{ ...btnBase, flex: 1, fontWeight: 600 }}>Cancel</Button>
        <Button onClick={onDiscard} variant="outlined" disabled={saving} color="error" sx={{ ...btnBase, flex: 1 }}>Discard</Button>
        <Button onClick={onSave} variant="contained" disabled={saving} sx={{ ...btnBase, ...ctaNavy, flex: 1 }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── New entity dialog ────────────────────────────────────────────────────────
function NewEntityDialog({ open, onClose, onSubmit }) {
  const [name, setName] = useState('');
  const isValid = name.trim().length > 0;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSubmit(name.trim());
    setName('');
  };
  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 3, p: 1 } }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>Register New Enterprise Tenant</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <TextField required label="Enterprise Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Corp" fullWidth size="small"
            helperText={!isValid ? 'Required' : ''}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="outlined" sx={{ ...btnBase, fontWeight: 600 }}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!isValid} sx={{ ...btnBase, ...ctaGreen }}>Create Entity</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ─── New space dialog ─────────────────────────────────────────────────────────
function NewSpaceDialog({ open, entityName, languageOptions = [], currencyOptions = [], onClose, onSubmit }) {
  const [name, setName] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [locales, setLocales] = useState([]);

  const isValid = name.trim().length > 0 && Boolean(currencyId) && locales.length > 0 && locales.some((l) => l.isDefault);

  const handleToggleLang = (localeId, displayName) => {
    const exists = locales.some((l) => l.localeId === localeId);
    if (exists) {
      const remaining = locales.filter((l) => l.localeId !== localeId);
      const wasDefault = locales.find((l) => l.localeId === localeId)?.isDefault;
      if (wasDefault && remaining.length > 0) remaining[0] = { ...remaining[0], isDefault: true };
      setLocales(remaining);
    } else {
      setLocales([...locales, { localeId, displayName, isDefault: locales.length === 0 }]);
    }
  };

  const handleSetDefaultLang = (localeId) => {
    setLocales((prev) => prev.map((l) => ({ ...l, isDefault: l.localeId === localeId })));
  };

  const reset = () => { setName(''); setCurrencyId(''); setLocales([]); };
  const handleClose = () => { reset(); onClose(); };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSubmit(name.trim(), currencyId, locales.map(({ localeId, isDefault }) => ({ localeId, isDefault })));
    reset();
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 3, p: 1 } }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>Create Space{entityName ? ` — ${entityName}` : ''}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <TextField required label="Space Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Main Exhibition Hall" fullWidth size="small"
            helperText={name.trim().length === 0 ? 'Required' : ''}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

          <FormControl required fullWidth size="small">
            <InputLabel id="new-space-currency-label">Default Currency</InputLabel>
            <Select labelId="new-space-currency-label" label="Default Currency" value={currencyId}
              onChange={(e) => setCurrencyId(e.target.value)}
              sx={{ borderRadius: 2 }}>
              {currencyOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.ISO} ({c.Symbol}) — {c.Name}</MenuItem>
              ))}
            </Select>
            {!currencyId && <FormHelperText>Required</FormHelperText>}
          </FormControl>

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Supported Languages
            </Typography>
            <Typography variant="body2" sx={{ color: C.muted, mt: 0.5, mb: 1.5 }}>
              Select at least one language and mark one as default.
            </Typography>
            {languageOptions.length === 0 ? (
              <Typography variant="body2" sx={{ color: C.muted }}>Loading languages…</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                {languageOptions.map((lang) => {
                  const isSelected = locales.some((l) => l.localeId === lang.localeId);
                  const isDefault  = locales.find((l) => l.localeId === lang.localeId)?.isDefault ?? false;
                  return (
                    <Paper key={lang.localeId} elevation={0}
                      onClick={() => handleToggleLang(lang.localeId, lang.displayName)}
                      sx={{
                        border: `1px solid ${isSelected ? '#3b82f6' : C.border}`,
                        borderRadius: 2, p: 1, cursor: 'pointer',
                        bgcolor: isSelected ? '#eff6ff' : '#fff',
                        transition: 'border-color 0.15s, background 0.15s',
                        '&:hover': { borderColor: '#3b82f6', bgcolor: isSelected ? '#eff6ff' : '#f8fafc' },
                      }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <Checkbox checked={isSelected} size="small" sx={{ p: 0 }}
                            onChange={() => handleToggleLang(lang.localeId, lang.displayName)}
                            onClick={(e) => e.stopPropagation()} />
                          <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: isSelected ? '#1e40af' : C.navy, fontSize: 13 }}>
                            {lang.displayName}
                          </Typography>
                        </Box>
                        {isSelected && (
                          <Tooltip title={isDefault ? 'Default language' : 'Set as default'}>
                            <Box component="button" type="button"
                              onClick={(e) => { e.stopPropagation(); handleSetDefaultLang(lang.localeId); }}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0,
                                border: 'none', cursor: 'pointer', px: 0.75, py: 0.25, borderRadius: 999,
                                bgcolor: isDefault ? '#fef3c7' : '#f1f5f9',
                                color: isDefault ? '#92400e' : '#475569',
                                fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: isDefault ? '#fde68a' : '#e2e8f0' },
                              }}>
                              {isDefault
                                ? <RadioButtonCheckedIcon sx={{ fontSize: 13 }} />
                                : <RadioButtonUncheckedIcon sx={{ fontSize: 13 }} />}
                              {isDefault ? 'Default' : 'Set default'}
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
            {locales.length === 0 && (
              <Typography variant="caption" sx={{ color: '#dc2626', mt: 1, display: 'block' }}>At least one language is required</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ ...btnBase, fontWeight: 600 }}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!isValid} sx={{ ...btnBase, ...ctaGreen }}>Launch Space</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ─── Assign Entity Admin dialog (Global Admin only) ───────────────────────────
function AssignEntityAdminDialog({ open, entityName, allMembers, onClose, onSubmit }) {
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [mode, setMode] = useState('existing');

  const isValid = mode === 'existing' ? Boolean(selectedMemberId) : newEmail.trim().length > 0;
  const handleClose = () => { setSelectedMemberId(''); setNewEmail(''); setNewName(''); onClose(); };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    if (mode === 'existing') onSubmit({ type: 'existing', memberId: selectedMemberId });
    else onSubmit({ type: 'new', email: newEmail.trim(), name: newName.trim() });
    setSelectedMemberId(''); setNewEmail(''); setNewName('');
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 3, p: 1 } }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminPanelSettingsOutlinedIcon sx={{ color: '#1d4ed8' }} />
          Assign Entity Administrator
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 2, p: 2, mb: 2.5, display: 'flex', gap: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#3b82f6', mt: 0.2, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: '#1e40af', lineHeight: 1.6 }}>
            <strong>Entity Admin</strong> can add / remove members across all spaces inside <strong>{entityName}</strong> and assign Space Admin, Editor, or Viewer roles. Only one Entity Admin is active per entity.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
          {['existing', 'new'].map((m) => (
            <Chip key={m} label={m === 'existing' ? 'Existing Member' : 'Invite New Person'} onClick={() => setMode(m)} variant={mode === m ? 'filled' : 'outlined'}
              sx={{ fontWeight: 700, cursor: 'pointer', ...(mode === m ? { bgcolor: C.navy, color: '#fff' } : {}) }} />
          ))}
        </Stack>
        <form id="assign-ea-form" onSubmit={handleSubmit}>
          {mode === 'existing' ? (
            <FormControl fullWidth size="small">
              <InputLabel>Select Member</InputLabel>
              <Select value={selectedMemberId} label="Select Member" onChange={(e) => setSelectedMemberId(e.target.value)} sx={{ borderRadius: 2 }}>
                {allMembers.filter((m) => !m.isCurrentUser).map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: C.navy }}>{initials(m.name)}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{m.name}</Typography>
                        <Typography variant="caption" sx={{ color: C.muted }}>{m.email}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {!selectedMemberId && <FormHelperText>Required</FormHelperText>}
            </FormControl>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField required type="email" label="Email Address" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="admin@company.com" fullWidth size="small"
                helperText={newEmail.trim() === '' ? 'Required' : ''}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField label="Display Name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Full name" fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
          )}
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ ...btnBase, fontWeight: 600 }}>Cancel</Button>
        <Button form="assign-ea-form" type="submit" variant="contained" disabled={!isValid} sx={{ ...btnBase, ...ctaNavy }}>Assign Entity Admin</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Add User to Spaces dialog (Spaces List level — Entity Admin scope) ──────
// Shows all entity spaces with checkboxes, then "same role for all" or
// "different role per space" assignment. Each space card in "different" mode
// gets a left-border accent in its type colour so they look visually distinct.
function AddUserToSpacesDialog({ open, entitySpaces, availableRoles, onClose, onSubmit }) {
  const [email, setEmail]                     = useState('');
  const [name, setName]                       = useState('');
  const [selectedIds, setSelectedIds]         = useState([]);
  const [roleMode, setRoleMode]               = useState('same'); // 'same' | 'different'
  const [sameRole, setSameRole]               = useState('');
  const [perSpaceRole, setPerSpaceRole]       = useState({});

  const toggleSpace = (id) =>
    setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const setDiffRole = (spaceId, role) =>
    setPerSpaceRole((p) => ({ ...p, [spaceId]: role }));

  const selectedSpaces = entitySpaces.filter((s) => selectedIds.includes(s.id));

  const buildAssignments = () =>
    roleMode === 'same'
      ? Object.fromEntries(selectedIds.map((id) => [id, sameRole]))
      : Object.fromEntries(selectedIds.map((id) => [id, perSpaceRole[id] ?? 'Editor']));

  const reset = () => { setEmail(''); setName(''); setSelectedIds([]); setRoleMode('same'); setSameRole(availableRoles?.[availableRoles.length - 1] ?? ''); setPerSpaceRole({}); };
  const handleClose  = () => { reset(); onClose(); };
  const isValid = email.trim().length > 0 && selectedIds.length > 0;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSubmit({ email: email.trim(), name: name.trim(), assignments: buildAssignments() });
    reset();
  };

  const allChecked  = selectedIds.length === entitySpaces.length;
  const someChecked = selectedIds.length > 0 && !allChecked;
  const toggleAll   = () => setSelectedIds(allChecked ? [] : entitySpaces.map((s) => s.id));

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 3, p: 1 } }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddOutlinedIcon sx={{ color: '#1d4ed8' }} />
          Add / Invite User to Spaces
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 0.5 }}>
        <form id="add-to-spaces-form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Identity */}
            <TextField required type="email" label="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" fullWidth size="small"
              helperText={email.trim() === '' ? 'Required' : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Display Name" helperText="Leave blank to send an email invitation instead" value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            {/* Space selection */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Select Spaces
                </Typography>
                <FormControlLabel
                  control={<Checkbox size="small" checked={allChecked} indeterminate={someChecked} onChange={toggleAll} sx={{ py: 0 }} />}
                  label={<Typography variant="caption" sx={{ fontWeight: 700, color: C.muted }}>Select all</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 180, overflowY: 'auto', pr: 0.5 }}>
                {entitySpaces.map((space) => {
                  const checked = selectedIds.includes(space.id);
                  return (
                    <Box key={space.id} onClick={() => toggleSpace(space.id)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, borderRadius: 2, border: `1px solid ${checked ? '#3b82f6' : C.border}`, bgcolor: checked ? '#eff6ff' : '#f8fafc', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { borderColor: '#3b82f6', bgcolor: '#eff6ff' } }}>
                      <Checkbox size="small" checked={checked} onChange={() => {}} sx={{ p: 0, color: '#94a3b8', '&.Mui-checked': { color: '#3b82f6' } }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: C.navy, lineHeight: 1 }}>{space.name}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Role assignment — only shown when ≥1 space selected */}
            {selectedIds.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.25 }}>
                  Role Assignment
                </Typography>

                {/* Same / different toggle */}
                <RadioGroup row value={roleMode} onChange={(e) => setRoleMode(e.target.value)} sx={{ mb: 1.5, gap: 2 }}>
                  <FormControlLabel value="same" control={<Radio size="small" />}
                    label={<Typography variant="body2" fontWeight={700}>Same role for all</Typography>} />
                  <FormControlLabel value="different" control={<Radio size="small" />}
                    label={<Typography variant="body2" fontWeight={700}>Different per space</Typography>} />
                </RadioGroup>

                {/* Same-role dropdown */}
                {roleMode === 'same' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: `1px solid ${C.border}` }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: C.muted, flexGrow: 1 }}>
                      Assign to all {selectedIds.length} selected space{selectedIds.length !== 1 ? 's' : ''}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select value={sameRole} onChange={(e) => setSameRole(e.target.value)} sx={{ borderRadius: 1.5, fontSize: 13 }}>
                        {(availableRoles ?? []).map((r) => <MenuItem key={r} value={r} sx={{ fontSize: 13 }}>{r}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Per-space role cards — visually distinct by type colour */}
                {roleMode === 'different' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 220, overflowY: 'auto', pr: 0.5 }}>
                    {selectedSpaces.map((space) => {
                      return (
                        <Box key={space.id}
                          sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5, borderRadius: 2, bgcolor: '#f8fafc', borderLeft: '4px solid #94a3b8', border: '1px solid #94a3b840', borderLeftWidth: 4 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: C.navy, lineHeight: 1, mb: 0.5 }}>{space.name}</Typography>
                          </Box>
                          <FormControl size="small" sx={{ minWidth: 155 }}>
                            <Select value={perSpaceRole[space.id] ?? 'Editor'} onChange={(e) => setDiffRole(space.id, e.target.value)} sx={{ borderRadius: 1.5, fontSize: 12, bgcolor: '#fff' }}>
                              {(availableRoles ?? []).map((r) => <MenuItem key={r} value={r} sx={{ fontSize: 13 }}>{r}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ ...btnBase, fontWeight: 600 }}>Cancel</Button>
        <Button form="add-to-spaces-form" type="submit" variant="contained" disabled={!isValid}
          sx={{ ...btnBase, ...ctaNavy }}>
          {name ? `Add to ${selectedIds.length} Space${selectedIds.length !== 1 ? 's' : ''}` : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Space Add Member dialog (Space Detail level) ─────────────────────────────
// Single space — no space picker needed. Role is scoped to this space only.
// availableRoles: Space Admin + Editor + Viewer for Global/Entity Admin;
//                 Editor + Viewer only for Space Admin.
function SpaceAddMemberDialog({ open, space, availableRoles, onClose, onSubmit }) {
  const [email, setEmail] = useState('');
  const [name, setName]   = useState('');
  const [role, setRole]   = useState(availableRoles?.[0] ?? 'Editor');

  const accent = { border: '#94a3b8', bg: '#f8fafc' };
  const isValid = email.trim().length > 0 && Boolean(role);
  const handleClose  = () => { setEmail(''); setName(''); setRole(availableRoles?.[0] ?? 'Editor'); onClose(); };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSubmit({ email: email.trim(), name: name.trim(), role });
    setEmail(''); setName(''); setRole(availableRoles?.[0] ?? 'Editor');
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 3, p: 1 } }} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddOutlinedIcon sx={{ color: '#1d4ed8' }} />
          Add Member to Space
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 0.5 }}>
        {space && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderRadius: 2, bgcolor: accent.bg, borderLeft: `4px solid ${accent.border}`, mb: 2.5, border: `1px solid ${accent.border}40`, borderLeftWidth: 4 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 800, color: C.navy }}>{space.name}</Typography>
              <Typography variant="caption" sx={{ color: C.muted }}>Role assigned to this space only</Typography>
            </Box>
          </Box>
        )}
        <form id="space-add-member-form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField required type="email" label="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" fullWidth size="small"
              helperText={email.trim() === '' ? 'Required' : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Display Name" helperText="Leave blank to send an invitation" value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <FormControl fullWidth size="small">
              <InputLabel>Assign Role</InputLabel>
              <Select value={role} label="Assign Role" onChange={(e) => setRole(e.target.value)} sx={{ borderRadius: 2 }}>
                {(availableRoles ?? []).map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ ...btnBase, fontWeight: 600 }}>Cancel</Button>
        <Button form="space-add-member-form" type="submit" variant="contained" disabled={!isValid} sx={{ ...btnBase, ...ctaNavy }}>
          {name ? 'Add Member' : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Manage Roles Dialog ────────────────────────────────────────────────────
function ManageRolesDialog({ open, member, roles, spaceId, onClose, onUpdate }) {
  const [draftRoles,    setDraftRoles]    = useState([]);
  const [draftExcluded, setDraftExcluded] = useState(false);
  const [addingRoleId,  setAddingRoleId]  = useState('');
  const [saving,        setSaving]        = useState(false);

  useEffect(() => {
    if (member) {
      setDraftRoles((member.roles ?? []).map((r) => ({ name: r.roleName, id: r.roleId })));
      setDraftExcluded(!!member.isExcludedFromApproval);
      setAddingRoleId('');
    }
  }, [member]);

  if (!member) return null;

  const draftRoleIds   = draftRoles.map((r) => r.id);
  const originalIds    = (member.roles ?? []).map((r) => r.roleId).slice().sort().join(',');
  const rolesChanged     = draftRoleIds.slice().sort().join(',') !== originalIds;
  const exclusionChanged = draftExcluded !== !!member.isExcludedFromApproval;
  const hasChanged     = rolesChanged || exclusionChanged;
  const availableToAdd = roles.filter((r) => !draftRoleIds.includes(r.id));

  // Stages a role addition locally — no API call until Save
  const handleAdd = () => {
    if (!addingRoleId) return;
    const role = roles.find((r) => r.id === parseInt(addingRoleId, 10));
    if (!role) return;
    setDraftRoles((p) => [...p, { name: role.name, id: role.id }]);
    setAddingRoleId('');
  };

  // Stages a role removal locally — no API call until Save
  const handleRemove = (roleId) => {
    if (draftRoles.length <= 1) return;
    setDraftRoles((p) => p.filter((r) => r.id !== roleId));
  };

  // Persists whichever of the two staged changes actually changed
  const handleSave = async () => {
    setSaving(true);
    try {
      if (rolesChanged)     await syncMemberRoles(spaceId, member.userId, draftRoleIds);
      if (exclusionChanged) await setMemberApprovalExclusion(spaceId, member.userId, draftExcluded);
      onUpdate();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 3, p: 1 } }} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Manage Roles
        <IconButton size="small" onClick={onClose}><CloseOutlinedIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
        <Box>
          <Typography variant="body2" fontWeight={700} color={C.navy}>{member.name}</Typography>
          <Typography variant="caption" color={C.muted}>{member.email}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" fontWeight={700} sx={{ color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
            Roles
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {draftRoles.map(({ name, id }) => (
              <Chip
                key={id ?? name}
                label={formatRole(name)}
                size="small"
                onDelete={draftRoles.length > 1 ? () => handleRemove(id) : undefined}
                sx={{ ...ROLE_STYLE[formatRole(name)] ?? ROLE_STYLE['Viewer'], fontWeight: 700, fontSize: 11 }}
              />
            ))}
          </Box>
          {draftRoles.length <= 1 && (
            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
              A member must keep at least one role.
            </Typography>
          )}
        </Box>

        {availableToAdd.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ flexGrow: 1 }}>
              <InputLabel>Add role</InputLabel>
              <Select value={addingRoleId} onChange={(e) => setAddingRoleId(e.target.value)} label="Add role" sx={{ borderRadius: 2 }}>
                {availableToAdd.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{formatRole(r.name)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleAdd} disabled={!addingRoleId || saving}
              sx={{ ...btnBase, ...ctaNavy, whiteSpace: 'nowrap' }}>
              Add
            </Button>
          </Box>
        )}

        <Divider />

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={draftExcluded}
                onChange={(e) => setDraftExcluded(e.target.checked)}
                disabled={saving}
              />
            }
            label={<Typography variant="body2" fontWeight={700} color={C.navy}>Exclude from content approvals</Typography>}
          />
          <Typography variant="caption" sx={{ color: C.muted, display: 'block', mt: 0.5 }}>
            When on, this member is skipped by the approval routing in this space only — other spaces
            they belong to are unaffected. Takes effect when you click Save Changes.
          </Typography>
          {member.isExcludedFromApproval && member.approvalExclusionChangedAt && (
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
              Set by {member.approvalExclusionChangedByName || 'unknown'} on{' '}
              {new Date(member.approvalExclusionChangedAt).toLocaleString()}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ ...btnBase }} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained"
          disabled={!hasChanged || saving || draftRoles.length === 0}
          sx={{ ...btnBase, ...ctaNavy }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════════
export default function AdminPortal() {
  const { user, activeSpace } = useAuth();
  const { refetchSpace } = useSpace();
  const spaceConfigCtx = useSpaceConfig();
  const { registerGuard } = useNavigationGuard();
  const { isGlobalAdmin, isEntityAdmin, isGmsAccountant, isSpaceAdmin, canFeature } = usePermissions();
  const navigate = useNavigate();

  // ── API-driven state ───────────────────────────────────────────────────────
  const [entities, setEntities]         = useState([]);
  const [spaces, setSpaces]             = useState([]);    // spaces for current entity
  const [entityName, setEntityName]     = useState('');   // name from GET /entities/{id}/spaces wrapper
  const [spaceMembers, setSpaceMembers] = useState([]);   // members for current space
  const [spaceName, setSpaceName]       = useState('');   // name from GET /spaces/{id}/members wrapper
  const [roles, setRoles]               = useState([]);   // space-scoped roles from API
  const [pendingInvitations, setPendingInvitations] = useState([]);  // API-driven pending invites
  const [pendingGmsInvitations, setPendingGmsInvitations] = useState([]);
  const [entityAdmins, setEntityAdmins] = useState([]);
  const [pendingEntityAdminInvitations, setPendingEntityAdminInvitations] = useState([]);
  const [gmsAccountants, setGmsAccountants] = useState([]);
  const [manageRolesMember, setManageRolesMember] = useState(null);

  const [spaceConfig, setSpaceConfig] = useState(null);
  const [savedFeatureFlags, setSavedFeatureFlags] = useState(null);
  const [savingFlags, setSavingFlags] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type: 'tab', value } | { type: 'nav', path } | { type: 'back' }
  // Currency saves go through the impact-preview dialog first, so the destination the admin
  // was heading to is parked here while that dialog is open and only followed once it saves.
  const [postCurrencySaveAction, setPostCurrencySaveAction] = useState(null);
  const [apiVersionInput, setApiVersionInput] = useState('1.0');
  const [entityConfig, setEntityConfig] = useState({ language: 'en-US', currency: 'USD', currencyId: null, localeId: 'en_us', contractPercentage: 0 });
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [configSaving, setConfigSaving] = useState(false);
  const [spaceLangs, setSpaceLangs]   = useState([]);
  const [langSaving, setLangSaving]   = useState(false);

  // Currency-as-originally-loaded, captured once per fetch so the save handlers can tell
  // whether the admin actually changed the currency selection (spaceConfig.currencyId /
  // entityConfig.currencyId are mutated immediately on every Select onChange, so by save
  // time they already hold the NEW value — these refs hold what was there before).
  const [originalEntityCurrencyId, setOriginalEntityCurrencyId] = useState(null);
  const [originalSpaceCurrencyId, setOriginalSpaceCurrencyId]   = useState(null);
  // The parent entity's default currency, shown on the space Currency tab so admins can see
  // what "inherit" resolves to. Kept separate from entityConfig, which belongs to the
  // entity-config view and would otherwise show stale data across entities.
  const [entityDefaultCurrencyId, setEntityDefaultCurrencyId]   = useState(null);

  // Currency-change impact-preview dialog — shared by both the Entity "Default Currency"
  // save and the Space "Space Currency" save, since both mutate the effective currency
  // existing items resolve against.
  const [currencyDialogOpen, setCurrencyDialogOpen]       = useState(false);
  const [currencyDialogFlow, setCurrencyDialogFlow]       = useState(null); // 'entity' | 'space'
  const [currencyImpact, setCurrencyImpact]               = useState(null);
  const [currencyImpactLoading, setCurrencyImpactLoading] = useState(false);

  const [view, setView]                     = useState('entities');
  const [activeEntityId, setActiveEntityId] = useState(null);
  const [activeSpaceId, setActiveSpaceId]   = useState(null);
  const [spaceTab, setSpaceTab]             = useState(0);
  const [searchTerm, setSearchTerm]         = useState('');
  const [quickEmail, setQuickEmail]         = useState('');
  const [quickRole, setQuickRole]           = useState('');
  const [toast, setToast]                   = useState(null);
  const [deleteTarget, setDeleteTarget]         = useState(null);
  const [deleteMemberTarget, setDeleteMemberTarget] = useState(null);
  const [deleteAdminTarget, setDeleteAdminTarget]   = useState(null); // { admin, type, entityId }
  const [modal, setModal]                   = useState(null);

  const showToast = useCallback((msg, sev = 'success') => setToast({ msg, sev }), []);

  // ── Role-based entry point ─────────────────────────────────────────────────
  useEffect(() => {
    if (isGmsAccountant) {
      // gms_accountant has no admin portal — redirect to their financial dashboard
      navigate('/payments');
      return;
    }
    if (isSpaceAdmin && !isGlobalAdmin && !isEntityAdmin && activeSpace?.spaceId) {
      // space_admin (with no higher system role) skips entity + space levels and goes
      // directly to their space. A global_admin/entity_admin who also happens to hold
      // space_admin somewhere must NOT be shortcut past their own top-level view — system
      // role outranks a per-space role, so it's checked first.
      setActiveSpaceId(activeSpace.spaceId);
      setView('space-detail');
    } else if (isEntityAdmin && user?.entityId) {
      // entity_admin skips entity level, loads spaces for their entity directly
      setActiveEntityId(user.entityId);
      setView('spaces');
    }
    // global_admin stays at default 'entities' view
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data loading ───────────────────────────────────────────────────────────
  useEffect(() => {
    // Only global_admin can fetch the entity list
    if (canFeature('/admin', 'entity-directory')) {
      getEntities().then(setEntities).catch(() => {});
    }
    getRoles().then((data) => {
      setRoles(data);
      // Default quick-invite role to the last (least-privileged) option
      if (data.length) setQuickRole(formatRole(data[data.length - 1].name));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeEntityId) return;
    getSpacesByEntity(activeEntityId)
      .then((data) => {
        setEntityName(data.entityName ?? '');
        setSpaces((data.spaces ?? []).map((s) => ({ ...s, entityId: data.entityId })));
      })
      .catch(() => {});
  }, [activeEntityId]);

  useEffect(() => {
    if (!activeSpaceId) return;
    setSpaceMembers([]);
    getSpaceMembers(activeSpaceId)
      .then((data) => {
        setSpaceName(data.spaceName ?? '');
        setSpaceMembers(data?.members ?? []);
      })
      .catch(() => {});
  }, [activeSpaceId]);

  // Quick invite is scoped to whichever space is open; clear stale input left over
  // from a previous space when navigating between spaces via the in-app Back button.
  useEffect(() => {
    setQuickEmail('');
    if (roles.length) setQuickRole(formatRole(roles[roles.length - 1].name));
  }, [activeSpaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeSpaceId) return;
    setPendingInvitations([]);
    getPendingInvitations(activeSpaceId).then(setPendingInvitations).catch(() => {});
  }, [activeSpaceId]);

  useEffect(() => {
    if (!isGlobalAdmin) return;
    setPendingGmsInvitations([]);
    getPendingGmsAccountantInvitations().then(setPendingGmsInvitations).catch(() => {});
    getGmsAccountants().then(setGmsAccountants).catch(() => {});
  }, [isGlobalAdmin]);

  useEffect(() => {
    if (!activeEntityId || !isGlobalAdmin) return;
    setEntityAdmins([]);
    setPendingEntityAdminInvitations([]);
    getEntityAdmins(activeEntityId).then(setEntityAdmins).catch(() => {});
    getPendingEntityAdminInvitations(activeEntityId).then(setPendingEntityAdminInvitations).catch(() => {});
  }, [activeEntityId, isGlobalAdmin]);

  useEffect(() => {
    if (!activeSpaceId || (spaceTab !== 1 && spaceTab !== 3) || !isGlobalAdmin) return;
    setSpaceConfig(null);
    getSpaceConfig(activeSpaceId).then((cfg) => {
      setSpaceConfig(cfg);
      setSavedFeatureFlags(pickFeatureFlags(cfg));
      setOriginalSpaceCurrencyId(cfg?.currencyId ?? null);
      setApiVersionInput(cfg?.apiVersion ?? '1.0');
    }).catch(() => {});
  }, [activeSpaceId, spaceTab, isGlobalAdmin]);

  useEffect(() => {
    if (!activeSpaceId || spaceTab !== 2 || (!isGlobalAdmin && !isEntityAdmin)) return;
    getSpaceSupportedLanguages(activeSpaceId).then(setSpaceLangs).catch(() => {});
    if (languageOptions.length === 0)
      getLanguages().then(setLanguageOptions).catch(() => {});
  }, [activeSpaceId, spaceTab, isGlobalAdmin, isEntityAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (spaceTab !== 3 || !isGlobalAdmin) return;
    if (currencyOptions.length === 0)
      getCurrencies().then(setCurrencyOptions).catch(() => {});
  }, [spaceTab, isGlobalAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeSpaceId || spaceTab !== 3 || !activeEntityId || !isGlobalAdmin) return;
    setEntityDefaultCurrencyId(null);
    getEntityConfig(activeEntityId)
      .then((cfg) => setEntityDefaultCurrencyId(cfg?.currencyId ?? null))
      .catch(() => {});
  }, [activeSpaceId, spaceTab, activeEntityId, isGlobalAdmin]);

  useEffect(() => {
    // The Create Space dialog's currency/language pickers need these lists too —
    // fetch them as soon as the Spaces List loads rather than waiting for the
    // admin to have already visited a space's Languages or Currency tab.
    if (view !== 'spaces') return;
    if (languageOptions.length === 0) getLanguages().then(setLanguageOptions).catch(() => {});
    if (currencyOptions.length === 0) getCurrencies().then(setCurrencyOptions).catch(() => {});
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view !== 'entity-config' || !activeEntityId || !isGlobalAdmin) return;
    getEntityConfig(activeEntityId)
      .then((cfg) => {
        setEntityConfig(cfg);
        setOriginalEntityCurrencyId(cfg?.currencyId ?? null);
      })
      .catch(() => {});
    if (currencyOptions.length === 0)
      getCurrencies().then((list) => setCurrencyOptions(list)).catch(() => {});
    if (languageOptions.length === 0)
      getLanguages().then((list) => setLanguageOptions(list)).catch(() => {});
  }, [view, activeEntityId, isGlobalAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived state ──────────────────────────────────────────────────────────
  const currentEntity = useMemo(() => entities.find((e) => e.id === activeEntityId) ?? null,  [entities, activeEntityId]);
  const currentSpace  = useMemo(() => spaces.find((s) => s.id === activeSpaceId) ?? null,     [spaces, activeSpaceId]);
  const entitySpaces  = useMemo(() => spaces.filter((s) => s.entityId === activeEntityId),    [spaces, activeEntityId]);

  // entity_admin never loads the global entities list (global_admin only).
  // Provide a synthetic entity so the spaces view still renders.
  const resolvedEntity = useMemo(() => {
    if (currentEntity) return currentEntity;
    if (isEntityAdmin && activeEntityId) return { id: activeEntityId, name: entityName || 'Your Organization' };
    return null;
  }, [currentEntity, isEntityAdmin, activeEntityId, entityName]);

  // space_admin enters space-detail directly without loading entity/spaces lists.
  // Provide a synthetic space object so the view still renders.
  const resolvedSpace = useMemo(() => {
    if (currentSpace) return currentSpace;
    if (isSpaceAdmin && activeSpace?.spaceId) return { id: activeSpace.spaceId, name: spaceName || `Space ${activeSpace.spaceId}` };
    return null;
  }, [currentSpace, isSpaceAdmin, activeSpace?.spaceId, spaceName]);

  const refreshMembers = useCallback(async () => {
    if (!activeSpaceId) return;
    const updated = await getSpaceMembers(activeSpaceId);
    setSpaceMembers(updated?.members ?? []);
  }, [activeSpaceId]);

  const refreshPendingInvitations = useCallback(async () => {
    if (!activeSpaceId) return;
    const list = await getPendingInvitations(activeSpaceId);
    setPendingInvitations(list);
  }, [activeSpaceId]);

  const refreshPendingGmsInvitations = useCallback(async () => {
    const list = await getPendingGmsAccountantInvitations().catch(() => []);
    setPendingGmsInvitations(list);
  }, []);

  const refreshGmsAccountants = useCallback(async () => {
    const list = await getGmsAccountants().catch(() => []);
    setGmsAccountants(list);
  }, []);

  const refreshEntityAdmins = useCallback(async () => {
    if (!activeEntityId) return;
    const list = await getEntityAdmins(activeEntityId).catch(() => []);
    setEntityAdmins(list);
  }, [activeEntityId]);

  const refreshPendingEntityAdminInvitations = useCallback(async () => {
    if (!activeEntityId) return;
    const list = await getPendingEntityAdminInvitations(activeEntityId).catch(() => []);
    setPendingEntityAdminInvitations(list);
  }, [activeEntityId]);

  // API roles as display names for dropdowns
  const spaceRoleNames = useMemo(() => roles.map((r) => formatRole(r.name)), [roles]);

  const activeMembers = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return spaceMembers.filter((m) =>
      !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
    );
  }, [spaceMembers, searchTerm]);

  const pendingInvites = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return pendingInvitations.filter((i) =>
      !q || i.email.toLowerCase().includes(q),
    );
  }, [pendingInvitations, searchTerm]);

  const memberCount       = (space) => space.memberCount ?? 0;
  const entityMemberCount = (entity) => entity.memberCount ?? 0;

  // ── Helper: map display role name → role_id ────────────────────────────────
  const getRoleId = (displayName) => roles.find((r) => formatRole(r.name) === displayName)?.id ?? 0;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCreateEntity = async (name) => {
    if (!name) { showToast('Name required', 'error'); return; }
    try {
      const entity = await createEntityApi(name);
      setEntities((p) => [...p, entity]);
      setModal(null);
      setActiveEntityId(entity.id);
      setView('spaces');
      showToast(`Entity "${name}" created`);
    } catch (_) { /* api-error event handled globally by axios interceptor */ }
  };

  const handleCreateSpace = async (name, currencyId, locales) => {
    if (!name) { showToast('Name required', 'error'); return; }
    try {
      const space = await createSpaceApi(activeEntityId, name, currencyId, locales);
      setSpaces((p) => [...p, { ...space, entityId: space.entityId }]);
      setModal(null);
      showToast(`Space "${name}" created`);
    } catch (_) {}
  };

  const handleInviteGmsAccountant = async ({ email, name: displayName }) => {
    if (!email) { setModal(null); return; }
    try {
      const result = await inviteGmsAccountant({ email, name: displayName || undefined });
      if (result.hasActiveSpaces) {
        showToast(`${displayName || email} is an active member of one or more spaces — remove them from all spaces first`, 'error');
      } else {
        showToast(result.existingUser
          ? `${displayName || email} is now a GMS Accountant`
          : `Invite sent to ${email}`);
        if (result.existingUser) await refreshGmsAccountants();
        else await refreshPendingGmsInvitations();
      }
    } catch (_) {
      showToast('Failed to invite GMS Accountant', 'error');
    }
    setModal(null);
  };

  const handleAssignEntityAdmin = async ({ type, email, name: displayName }) => {
    const targetEmail = type === 'new' ? email : null;
    if (!targetEmail || !activeEntityId) { setModal(null); return; }
    try {
      const result = await assignEntityAdmin(activeEntityId, { email: targetEmail, name: displayName || undefined });
      if (result.isEntityAdminElsewhere) {
        showToast(`${displayName || targetEmail} is already Entity Admin for a different entity — remove that role first`, 'error');
      } else if (result.hasSpaceMembershipOutsideEntity) {
        showToast(`${displayName || targetEmail} already has space membership in a different entity — remove that first`, 'error');
      } else {
        showToast(result.existingUser
          ? `${displayName || targetEmail} is now entity admin`
          : `Invite sent to ${targetEmail}`);
        if (result.existingUser) await refreshEntityAdmins();
        else await refreshPendingEntityAdminInvitations();
      }
    } catch (_) {
      showToast('Failed to assign entity admin', 'error');
    }
    setModal(null);
  };

  // Shared with handleAddUser / handleAddMemberToSpace / handleQuickInvite below.
  const gmsAccountantBlockedMessage = (who) => `${who} is a GMS Accountant and cannot be added to spaces`;
  const entityAdminBlockedMessage   = (who) => `${who} is an Entity Admin for a different entity and cannot be added to this space`;

  // Used by AddUserToSpacesDialog (spaces-list level) — per-space assignments
  const handleAddUser = async ({ email, name, assignments }) => {
    if (!email || !Object.keys(assignments).length) { showToast('Email + at least one space required', 'error'); return; }
    let addedCount = 0;
    let invitedCount = 0;
    let alreadyInvitedCount = 0;
    let blockedReason = null; // 'gmsAccountant' | 'conflictingEntityAdmin' | null
    try {
      for (const [spaceId, roleName] of Object.entries(assignments)) {
        const roleId = getRoleId(roleName);
        if (!roleId) continue;
        const result = await inviteMember(parseInt(spaceId, 10), { email, name: name || undefined, roleId });
        // Blocking is per-email, not per-space — every remaining space would fail
        // identically, so stop here rather than repeat calls that can't succeed.
        if (result.isGmsAccountant) { blockedReason = 'gmsAccountant'; break; }
        if (result.isConflictingEntityAdmin) { blockedReason = 'conflictingEntityAdmin'; break; }
        if (result.alreadyInvited) alreadyInvitedCount += 1;
        else if (result.existingUser) addedCount += 1;
        else invitedCount += 1;
      }
      if (blockedReason) {
        const done = addedCount + invitedCount + alreadyInvitedCount;
        const reasonMsg = blockedReason === 'gmsAccountant'
          ? gmsAccountantBlockedMessage(name || email)
          : entityAdminBlockedMessage(name || email);
        showToast(
          done > 0 ? `${reasonMsg} — stopped after ${done} space(s), the rest were skipped` : reasonMsg,
          'error',
        );
      } else if (invitedCount > 0) {
        showToast(`Invite sent to ${email}`);
      } else if (addedCount > 0) {
        showToast(`${name || email} added to ${addedCount} space(s)`);
      } else {
        showToast(`${name || email} already has a pending invite to the selected space(s)`, 'error');
      }
    } catch (_) {
      showToast('Failed to add user to spaces', 'error');
    }
    setModal(null);
  };

  // Used by SpaceAddMemberDialog (space-detail level) — single space + single role
  const handleAddMemberToSpace = async ({ email, name, role }) => {
    if (!email || !role) return;
    const roleId = getRoleId(role);
    if (!roleId) { showToast('Invalid role', 'error'); return; }
    try {
      const result = await inviteMember(activeSpaceId, { email, name: name || undefined, roleId });
      if (result.isGmsAccountant) {
        showToast(gmsAccountantBlockedMessage(name || email), 'error');
      } else if (result.isConflictingEntityAdmin) {
        showToast(entityAdminBlockedMessage(name || email), 'error');
      } else if (result.alreadyInvited) {
        showToast(`${result.inviteeName || name || email} already has a pending invite to this space`, 'error');
      } else if (result.existingUser) {
        const updated = await getSpaceMembers(activeSpaceId);
        setSpaceMembers(updated?.members ?? []);
        showToast(`${name || email} added as ${role}`);
      } else {
        await refreshPendingInvitations();
        showToast(`Invite sent to ${email}`);
      }
    } catch (_) {
      showToast('Failed to add member', 'error');
    }
    setModal(null);
  };

  const isQuickInviteValid = quickEmail.trim().length > 0 && Boolean(quickRole);

  const handleQuickInvite = async (e) => {
    e.preventDefault();
    if (!isQuickInviteValid) return;
    const roleId = getRoleId(quickRole);
    if (!roleId) return;
    try {
      const result = await inviteMember(activeSpaceId, { email: quickEmail.trim(), roleId });
      if (result.isGmsAccountant) {
        showToast(gmsAccountantBlockedMessage(quickEmail), 'error');
        return;
      }
      if (result.isConflictingEntityAdmin) {
        showToast(entityAdminBlockedMessage(quickEmail), 'error');
        return;
      }
      if (result.alreadyInvited) {
        showToast(`${result.inviteeName || quickEmail} already has a pending invite to this space`, 'error');
        return;
      }
      if (result.existingUser) {
        const updated = await getSpaceMembers(activeSpaceId);
        setSpaceMembers(updated?.members ?? []);
      } else {
        await refreshPendingInvitations();
      }
      setQuickEmail('');
      showToast(`Invite sent to ${quickEmail}`);
    } catch (_) {
      showToast('Failed to send invite', 'error');
    }
  };

  const handleRevoke = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'member') {
      try {
        await removeMember(activeSpaceId, deleteTarget.id);
        setSpaceMembers((p) => p.filter((m) => m.userId !== deleteTarget.id));
        showToast(`Removed ${deleteTarget.label}`);
      } catch (_) {}
    } else if (deleteTarget.type === 'gms-invite') {
      try {
        await revokeGmsAccountantInvitation(deleteTarget.id);
        await refreshPendingGmsInvitations();
        showToast(`Revoked invite for ${deleteTarget.label}`);
      } catch (_) {}
    } else if (deleteTarget.type === 'entity-admin-invite') {
      try {
        await revokePendingEntityAdminInvitation(activeEntityId, deleteTarget.id);
        await refreshPendingEntityAdminInvitations();
        showToast(`Revoked invite for ${deleteTarget.label}`);
      } catch (_) {}
    } else {
      try {
        await revokePendingInvitation(activeSpaceId, deleteTarget.id);
        await refreshPendingInvitations();
        showToast(`Revoked invite for ${deleteTarget.label}`);
      } catch (_) {}
    }
    setDeleteTarget(null);
  };

  // Stages the toggle locally only — no API call until Save is clicked.
  const handleSpaceConfigToggle = (key, value) => {
    setSpaceConfig((p) => ({ ...p, [key]: value }));
  };

  const isFeatureFlagsDirty = useMemo(() => {
    if (!spaceConfig || !savedFeatureFlags) return false;
    return FEATURE_FLAG_KEYS.some((k) => !!spaceConfig[k] !== !!savedFeatureFlags[k]);
  }, [spaceConfig, savedFeatureFlags]);

  const isApiVersionDirty = apiVersionInput.trim() !== (spaceConfig?.apiVersion ?? '1.0');
  const isFeatureSectionDirty = isApiVersionDirty || isFeatureFlagsDirty;

  // Compared against what was last loaded/saved from the server, not the select's initial value,
  // so picking a currency and then picking the original one again is not "dirty".
  const isCurrencyDirty = !!spaceConfig && (spaceConfig.currencyId ?? null) !== originalSpaceCurrencyId;
  const isActiveTabDirty = (spaceTab === 1 && isFeatureSectionDirty) || (spaceTab === 3 && isCurrencyDirty);

  const runPendingAction = (action) => {
    if (action?.type === 'tab') setSpaceTab(action.value);
    else if (action?.type === 'nav') navigate(action.path);
    else if (action?.type === 'back') { setView('spaces'); setActiveSpaceId(''); }
  };

  // One Save button for the whole Feature Flags tab — persists the toggles and the API
  // Version together in a single call, instead of two separate save actions.
  const handleSaveFeatureSection = async () => {
    if (!apiVersionInput.trim()) return false;
    setSavingFlags(true);
    try {
      const saved = await updateSpaceConfig(activeSpaceId, { ...spaceConfig, apiVersion: apiVersionInput.trim() });
      setSpaceConfig(saved);
      setSavedFeatureFlags(pickFeatureFlags(saved));
      setApiVersionInput(saved?.apiVersion ?? apiVersionInput.trim());
      // Push straight into the shared SpaceConfigContext so the Sidebar reflects the
      // change immediately — no full page reload needed (same idea as refetchSpace()
      // elsewhere on this page for currency changes).
      spaceConfigCtx?.setConfig?.(saved);
      showToast('Feature flags saved');
      return true;
    } catch (_) {
      showToast('Failed to save feature flags', 'error');
      return false;
    } finally {
      setSavingFlags(false);
    }
  };

  // Only guards navigation while the Feature Flags or Currency tab has unsaved edits —
  // registers/unregisters as that condition changes, and always unregisters on unmount.
  useEffect(() => {
    if (!isActiveTabDirty) return undefined;
    return registerGuard((path) => setPendingAction({ type: 'nav', path }));
  }, [isActiveTabDirty, registerGuard]);

  // Appends the currencyCascade summary to a base toast message when the save actually
  // triggered one (cascade is null when the save didn't change currency at all).
  const cascadeToastMessage = (baseMsg, cascade) => {
    if (!cascade) return baseMsg;
    const skippedPart = cascade.itemsSkipped ? `, ${cascade.itemsSkipped} skipped` : '';
    return `${baseMsg} — ${cascade.itemsUpdated} item(s) updated${skippedPart}`;
  };

  const closeCurrencyDialog = () => {
    setCurrencyDialogOpen(false);
    setCurrencyDialogFlow(null);
    setCurrencyImpact(null);
    setPostCurrencySaveAction(null);
  };

  const performEntityConfigSave = async (contractPercentage) => {
    setConfigSaving(true);
    try {
      const saved = await updateEntityConfig(activeEntityId, {
        currencyId: entityConfig.currencyId,
        localeId:   entityConfig.localeId,
        contractPercentage,
      });
      setEntityConfig(saved);
      setOriginalEntityCurrencyId(saved?.currencyId ?? null);
      showToast(cascadeToastMessage('Configuration saved', saved?.currencyCascade));
      // Entity's default currency feeds every space that inherits it — refetch so
      // Payments/Items reflect the change without a hard reload, same as the space-currency
      // save below (this call was previously missing here — see AdminPortal known-bug note).
      refetchSpace();
      return true;
    } catch (_) {
      showToast('Failed to save configuration', 'error');
      return false;
    } finally {
      setConfigSaving(false);
    }
  };

  const handleSaveEntityConfig = async () => {
    if (!entityConfig.currencyId) return;
    const contractPercentage = Number(entityConfig.contractPercentage ?? 0);
    if (Number.isNaN(contractPercentage) || contractPercentage < 0 || contractPercentage > 100) {
      showToast('Contract percentage must be a number between 0 and 100', 'error');
      return;
    }

    if (entityConfig.currencyId !== originalEntityCurrencyId) {
      setCurrencyDialogFlow('entity');
      setCurrencyImpact(null);
      setCurrencyDialogOpen(true);
      setCurrencyImpactLoading(true);
      try {
        const impact = await getEntityCurrencyImpact(activeEntityId, entityConfig.currencyId);
        setCurrencyImpact(impact);
      } catch (_) {
        closeCurrencyDialog();
        showToast('Failed to check currency change impact', 'error');
      } finally {
        setCurrencyImpactLoading(false);
      }
      return;
    }

    await performEntityConfigSave(contractPercentage);
  };

  const performSpaceCurrencySave = async () => {
    setConfigSaving(true);
    try {
      const updated = await updateSpaceConfig(activeSpaceId, { ...spaceConfig, currencyId: spaceConfig?.currencyId ?? null });
      setSpaceConfig(updated);
      setOriginalSpaceCurrencyId(updated?.currencyId ?? null);
      showToast(cascadeToastMessage('Currency saved', updated?.currencyCascade));
      // SpaceContext/CurrencyContext are fetched once per login/space-switch and won't
      // otherwise notice this edit — refetch so Payments/Items reflect it without a hard reload
      // when the editor is viewing their own active space.
      refetchSpace();
      return true;
    } catch (_) {
      showToast('Failed to save currency', 'error');
      return false;
    } finally {
      setConfigSaving(false);
    }
  };

  const handleSaveSpaceCurrency = async () => {
    const newCurrencyId = spaceConfig?.currencyId ?? null;

    if (newCurrencyId !== originalSpaceCurrencyId) {
      setCurrencyDialogFlow('space');
      setCurrencyImpact(null);
      setCurrencyDialogOpen(true);
      setCurrencyImpactLoading(true);
      try {
        const impact = await getSpaceCurrencyImpact(activeSpaceId, newCurrencyId);
        setCurrencyImpact(impact);
      } catch (_) {
        closeCurrencyDialog();
        showToast('Failed to check currency change impact', 'error');
      } finally {
        setCurrencyImpactLoading(false);
      }
      return;
    }

    await performSpaceCurrencySave();
  };

  // Single confirm handler for the shared dialog — branches on which flow opened it. Only
  // closes the dialog when the save actually succeeded — perform*Save's own try/catch shows
  // a failure toast but previously never signaled failure back here, so the dialog closed
  // regardless, silently discarding the admin's ability to retry without re-fetching impact.
  const handleConfirmCurrencyChange = async () => {
    let succeeded = false;
    if (currencyDialogFlow === 'entity') {
      const contractPercentage = Number(entityConfig.contractPercentage ?? 0);
      succeeded = await performEntityConfigSave(contractPercentage);
    } else if (currencyDialogFlow === 'space') {
      succeeded = await performSpaceCurrencySave();
    }
    if (succeeded) {
      const next = postCurrencySaveAction;
      closeCurrencyDialog();
      runPendingAction(next);
    }
  };

  const currencyIsoById = (id) => currencyOptions.find((c) => c.id === id)?.ISO;
  const entityDefaultCurrency = currencyOptions.find((c) => c.id === entityDefaultCurrencyId);
  const formatCurrency = (c) => `${c.ISO} (${c.Symbol}) — ${c.Name}`;
  // Shown only when the selected currency differs from the entity default — otherwise the
  // "Entity default" badge on the selected option already says everything. Follows the
  // dropdown selection (not the saved value).
  const spaceCurrencyStatus = (() => {
    const selectedId = spaceConfig?.currencyId;
    if (!entityDefaultCurrency || selectedId == null || selectedId === entityDefaultCurrency.id) return null;
    const own = currencyOptions.find((c) => c.id === selectedId);
    return own ? `This space uses ${formatCurrency(own)}. The entity default is ${formatCurrency(entityDefaultCurrency)}.` : null;
  })();
  // Unsaved-changes popup copy: names the pending choice and the saved value that Discard goes
  // back to. A space with no currency of its own is saved as "following the entity default".
  const unsavedCurrencyMessage = (() => {
    const labelOf = (id) => { const c = currencyOptions.find((o) => o.id === id); return c ? formatCurrency(c) : null; };
    const savedLabel = originalSpaceCurrencyId != null ? labelOf(originalSpaceCurrencyId)
      : (entityDefaultCurrency ? formatCurrency(entityDefaultCurrency) : null);
    const pendingLabel = labelOf(spaceConfig?.currencyId ?? entityDefaultCurrencyId);
    if (!savedLabel || !pendingLabel) return undefined;
    return `You changed the currency to ${pendingLabel} but haven't saved it. Save it, or discard to go back to ${savedLabel}.`;
  })();
  const currencyDialogOldIso = currencyDialogFlow === 'entity'
    ? currencyIsoById(originalEntityCurrencyId)
    : currencyIsoById(originalSpaceCurrencyId) ?? 'Inherited';
  const currencyDialogNewIso = currencyDialogFlow === 'entity'
    ? currencyIsoById(entityConfig.currencyId)
    : currencyIsoById(spaceConfig?.currencyId) ?? 'Inherited';

  const handleSaveSpaceLangs = async () => {
    setLangSaving(true);
    try {
      const locales = spaceLangs.map(({ localeId, isDefault }) => ({ localeId, isDefault }));
      const updated = await updateSpaceSupportedLanguages(activeSpaceId, locales);
      setSpaceLangs(updated);
      showToast('Languages saved');
    } catch (_) {
      showToast('Failed to save languages', 'error');
    } finally {
      setLangSaving(false);
    }
  };

  const handleToggleLang = (localeId, displayName) => {
    const exists = spaceLangs.some((l) => l.localeId === localeId);
    if (exists) {
      const remaining = spaceLangs.filter((l) => l.localeId !== localeId);
      if (remaining.length === 0) return;
      const wasDefault = spaceLangs.find((l) => l.localeId === localeId)?.isDefault;
      if (wasDefault) remaining[0] = { ...remaining[0], isDefault: true };
      setSpaceLangs(remaining);
    } else {
      setSpaceLangs([...spaceLangs, { localeId, displayName, isDefault: false }]);
    }
  };

  const handleSetDefaultLang = (localeId) => {
    setSpaceLangs((prev) => prev.map((l) => ({ ...l, isDefault: l.localeId === localeId })));
  };

  const Toaster = () => (
    <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Alert severity={toast?.sev ?? 'success'} variant="filled" sx={{ borderRadius: 2 }}>{toast?.msg}</Alert>
    </Snackbar>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // LEVEL 1 — Entity Directory
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'entities') return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Toaster />
      <ConfirmDialog open={!!deleteTarget} target={deleteTarget} onConfirm={handleRevoke} onCancel={() => setDeleteTarget(null)} />
      <NewEntityDialog open={modal === 'entity'} onClose={() => setModal(null)} onSubmit={handleCreateEntity} />
      <InviteGmsAccountantDialog open={modal === 'gms-accountant'} onClose={() => setModal(null)} onSubmit={handleInviteGmsAccountant} />
      <DeleteAdminDialog
        open={!!deleteAdminTarget}
        admin={deleteAdminTarget?.admin}
        type={deleteAdminTarget?.type}
        entityId={deleteAdminTarget?.entityId}
        onClose={() => setDeleteAdminTarget(null)}
        onRemoved={() => { refreshGmsAccountants(); showToast(`${deleteAdminTarget?.admin?.name || deleteAdminTarget?.admin?.email || 'User'} removed as GMS Accountant`); }}
        onDeleted={() => { refreshGmsAccountants(); showToast('Account permanently deleted'); }}
      />

      <SectionHeader
        title="Enterprise Organization Directory"
        subtitle="Select a tenant organization to manage its spaces and member directories."
        action={
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {canFeature('/admin', 'invite-gms-accountant') && (
              <Button variant="outlined" startIcon={<PersonAddOutlinedIcon />} onClick={() => setModal('gms-accountant')}
                sx={{ ...btnBase, borderColor: '#1d4ed8', color: '#1d4ed8', '&:hover': { bgcolor: '#eff6ff' } }}>
                Invite GMS Accountant
              </Button>
            )}
            {canFeature('/admin', 'create-entity') && (
              <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => setModal('entity')} sx={{ ...btnBase, ...ctaGreen, px: 2.5 }}>
                Create Entity
              </Button>
            )}
          </Box>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(auto-fill, minmax(290px, 1fr))' }, gap: 3 }}>
        {entities.map((ent, idx) => {
          const color = entityColor(idx);
          return (
            <Card key={ent.id} elevation={0} sx={{ ...cardBase }}>
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                  <Avatar sx={{ width: 52, height: 52, bgcolor: color, fontSize: 20, fontWeight: 900, letterSpacing: -1 }}>
                    {initials(ent.name)}
                  </Avatar>
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 800, color: C.navy, lineHeight: 1.2, mb: 0.75 }}>{ent.name}</Typography>

                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <LayersOutlinedIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: C.muted }}>{ent.spaceCount ?? 0} Spaces</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <PeopleOutlinedIcon sx={{ fontSize: 16, color: C.green }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: C.muted }}>{entityMemberCount(ent)} Members</Typography>
                  </Box>
                </Stack>

                <Button fullWidth variant="outlined" endIcon={<ChevronRightOutlinedIcon />}
                  onClick={() => { setActiveEntityId(ent.id); setView('spaces'); }}
                  sx={{ ...btnBase, borderColor: C.border, color: C.navy, '&:hover': { bgcolor: C.navy, color: '#fff', borderColor: C.navy } }}>
                  Manage Entity
                </Button>
              </Box>
            </Card>
          );
        })}
      </Box>

      {/* ── GMS Accountant Active List ── */}
      {canFeature('/admin', 'invite-gms-accountant') && gmsAccountants.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, overflow: 'hidden', mt: 3 }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.navy }}>Active GMS Accountants</Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>{gmsAccountants.length} accountant{gmsAccountants.length !== 1 ? 's' : ''} with system-level access</Typography>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 480 }}>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, py: 1.25 } }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gmsAccountants.map((acc) => (
                  <TableRow key={acc.userId} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& td': { borderColor: C.border, py: 1.5 } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#0891b2', fontSize: 13, fontWeight: 700 }}>{initials(acc.name)}</Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{acc.name || '—'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: C.muted, fontFamily: 'monospace' }}>{acc.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: C.muted }}>{acc.assignedAt ? new Date(acc.assignedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove GMS Accountant role">
                        <IconButton size="small"
                          onClick={() => setDeleteAdminTarget({ admin: acc, type: 'gms-accountant' })}
                          sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#fee2e2', color: '#dc2626' } }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {/* ── Pending GMS Accountant Invitations ── */}
      {canFeature('/admin', 'invite-gms-accountant') && pendingGmsInvitations.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, overflow: 'hidden', mt: 3 }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.navy }}>Pending GMS Accountant Invitations</Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>{pendingGmsInvitations.length} invite{pendingGmsInvitations.length !== 1 ? 's' : ''} awaiting acceptance</Typography>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, py: 1.25 } }}>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Invited By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Invited</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingGmsInvitations.map((inv) => (
                  <TableRow key={inv.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& td': { borderColor: C.border, py: 1.5 } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#f1f5f9' }}>
                          <EmailOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontFamily: 'monospace', fontSize: 13 }}>{inv.email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: C.muted }}>{inv.name || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: C.muted }}>{inv.invitedByName || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label="Pending" size="small"
                        sx={{ bgcolor: '#fff8e1', color: '#854d0e', fontWeight: 700, fontSize: 10, height: 20 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: C.muted }}>{new Date(inv.createdTime).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: C.muted }}>{new Date(inv.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Cancel invitation">
                        <IconButton size="small" onClick={() => setDeleteTarget({ type: 'gms-invite', id: inv.id, label: inv.email })}
                          sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#fee2e2', color: '#dc2626' } }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
    </Box>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // LEVEL 2 — Spaces List
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'spaces' && resolvedEntity) {
    const color = entityColor(Math.max(0, entities.findIndex((e) => e.id === activeEntityId)));
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Toaster />
        <ConfirmDialog open={!!deleteTarget} target={deleteTarget} onConfirm={handleRevoke} onCancel={() => setDeleteTarget(null)} />
        <NewSpaceDialog open={modal === 'space'} entityName={resolvedEntity.name} languageOptions={languageOptions} currencyOptions={currencyOptions} onClose={() => setModal(null)} onSubmit={handleCreateSpace} />
        <AddUserToSpacesDialog open={modal === 'add-user-to-spaces'} entitySpaces={entitySpaces} availableRoles={spaceRoleNames} onClose={() => setModal(null)} onSubmit={handleAddUser} />
        <InviteEntityAdminDialog open={modal === 'entity-admin'} entityName={resolvedEntity.name} onClose={() => setModal(null)} onSubmit={handleAssignEntityAdmin} />
        <DeleteAdminDialog
          open={!!deleteAdminTarget}
          admin={deleteAdminTarget?.admin}
          type={deleteAdminTarget?.type}
          entityId={deleteAdminTarget?.entityId}
          onClose={() => setDeleteAdminTarget(null)}
          onRemoved={() => { refreshEntityAdmins(); showToast(`${deleteAdminTarget?.admin?.name || deleteAdminTarget?.admin?.email || 'User'} removed as Entity Admin`); }}
          onDeleted={() => { refreshEntityAdmins(); showToast('Account permanently deleted'); }}
        />

        {/* Global admin can navigate back to entity list; entity_admin has no entity list to go back to */}
        {isGlobalAdmin && (
          <BackBtn label="All Organizations" onClick={() => { setView('entities'); setActiveEntityId(''); }} />
        )}

        {/* Entity header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 48, height: 48, bgcolor: color, fontSize: 18, fontWeight: 800 }}>{initials(resolvedEntity.name)}</Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: C.navy, lineHeight: 1 }}>{resolvedEntity.name}</Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1.5}>
            {isGlobalAdmin && (
              <Button variant="outlined" startIcon={<TuneOutlinedIcon />} onClick={() => setView('entity-config')}
                sx={{ ...btnBase, fontWeight: 600, borderColor: C.border, color: C.navy, '&:hover': { bgcolor: '#f1f5f9' } }}>
                Configure
              </Button>
            )}
            <Button variant="outlined" startIcon={<PersonAddOutlinedIcon />} onClick={() => setModal('add-user-to-spaces')}
              sx={{ ...btnBase, fontWeight: 600, borderColor: '#3b82f6', color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff', borderColor: '#2563eb' } }}>
              Add User to Spaces
            </Button>
            {canFeature('/admin', 'create-space') && (
              <Button variant="contained" startIcon={<CreateNewFolderOutlinedIcon />} onClick={() => setModal('space')}
                sx={{ ...btnBase, ...ctaGreen }}>
                Create Space
              </Button>
            )}
          </Stack>
        </Box>

        {/* Entity Admin panel */}
        {isGlobalAdmin && (
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, overflow: 'hidden', mb: 3 }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AdminPanelSettingsOutlinedIcon sx={{ color: '#1d4ed8', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: C.navy }}>Entity Administrators</Typography>
                  <Typography variant="caption" sx={{ color: C.muted }}>
                    {entityAdmins.length} active · {pendingEntityAdminInvitations.length} pending
                  </Typography>
                </Box>
              </Box>
              <Button variant="outlined" size="small" startIcon={<PersonAddOutlinedIcon />}
                onClick={() => setModal('entity-admin')}
                sx={{ ...btnBase, fontSize: 12, borderColor: '#1d4ed8', color: '#1d4ed8', '&:hover': { bgcolor: '#eff6ff' } }}>
                Invite Admin
              </Button>
            </Box>

            {entityAdmins.length > 0 && (
              <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 480 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, py: 1.25 } }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Assigned</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entityAdmins.map((adm) => (
                      <TableRow key={adm.userId} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& td': { borderColor: C.border, py: 1.5 } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1d4ed8', fontSize: 13, fontWeight: 700 }}>{initials(adm.name)}</Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{adm.name || '—'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: C.muted, fontFamily: 'monospace' }}>{adm.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: C.muted }}>{adm.assignedAt ? new Date(adm.assignedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove entity admin role">
                            <IconButton size="small"
                              onClick={() => setDeleteAdminTarget({ admin: adm, type: 'entity-admin', entityId: activeEntityId })}
                              sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#fee2e2', color: '#dc2626' } }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {entityAdmins.length === 0 && (
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography variant="caption" sx={{ color: C.muted }}>
                  No entity administrators assigned yet. Invite one to allow them to manage all spaces within {resolvedEntity.name}.
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Spaces grid */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.navy, mb: 0.5 }}>Workspace Spaces</Typography>
          <Typography variant="body2" sx={{ color: C.muted, mb: 3 }}>Click a space to manage its member directory and feature configuration.</Typography>

          {entitySpaces.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: C.muted }}>
              <LayersOutlinedIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography fontWeight={700}>No spaces yet</Typography>
              <Typography variant="body2">Click "Create Space" to add the first one.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(auto-fill, minmax(250px, 1fr))' }, gap: 2.5 }}>
              {entitySpaces.map((space) => (
                <Card key={space.id} elevation={0} sx={{ ...cardBase }}>
                  <Box sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ p: 1.25, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex' }}>
                        <LayersOutlinedIcon sx={{ fontSize: 20, color: '#475569' }} />
                      </Box>
                      <Chip label={`Space ID ${space.id}`} size="small"
                        sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: 11, height: 22, fontFamily: 'monospace' }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.navy, mb: 0.5 }}>{space.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2.5 }}>
                      <PeopleOutlinedIcon sx={{ fontSize: 14, color: C.green }} />
                      <Typography variant="caption" sx={{ color: C.muted, fontWeight: 600 }}>{memberCount(space)} Members</Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Button size="small" endIcon={<ChevronRightOutlinedIcon />}
                      onClick={() => { setActiveSpaceId(space.id); setSpaceTab(0); setSearchTerm(''); setView('space-detail'); }}
                      sx={{ textTransform: 'none', fontWeight: 700, color: '#3b82f6', p: 0, '&:hover': { bgcolor: 'transparent', color: '#1d4ed8' } }}>
                      Manage Directory
                    </Button>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </Paper>

        {/* ── Pending Entity Admin Invitations ── */}
        {isGlobalAdmin && pendingEntityAdminInvitations.length > 0 && (
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, overflow: 'hidden', mt: 3 }}>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.navy }}>Pending Entity Admin Invitations</Typography>
              <Typography variant="caption" sx={{ color: C.muted }}>{pendingEntityAdminInvitations.length} invite{pendingEntityAdminInvitations.length !== 1 ? 's' : ''} awaiting acceptance</Typography>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, py: 1.25 } }}>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Invited By</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invited</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingEntityAdminInvitations.map((inv) => (
                    <TableRow key={inv.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& td': { borderColor: C.border, py: 1.5 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#f1f5f9' }}>
                            <EmailOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontFamily: 'monospace', fontSize: 13 }}>{inv.email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="caption" sx={{ color: C.muted }}>{inv.name || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption" sx={{ color: C.muted }}>{inv.invitedByName || '—'}</Typography></TableCell>
                      <TableCell>
                        <Chip label="Pending" size="small"
                          sx={{ bgcolor: '#fff8e1', color: '#854d0e', fontWeight: 700, fontSize: 10, height: 20 }} />
                      </TableCell>
                      <TableCell><Typography variant="caption" sx={{ color: C.muted }}>{new Date(inv.createdTime).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Typography></TableCell>
                      <TableCell><Typography variant="caption" sx={{ color: C.muted }}>{new Date(inv.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Typography></TableCell>
                      <TableCell align="right">
                        <Tooltip title="Cancel invitation">
                          <IconButton size="small" onClick={() => setDeleteTarget({ type: 'entity-admin-invite', id: inv.id, label: inv.email })}
                            sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#fee2e2', color: '#dc2626' } }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        )}
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // LEVEL 3 — Space Detail
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'space-detail' && resolvedSpace) return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Toaster />
      <CurrencyChangeConfirmDialog
        open={currencyDialogOpen && currencyDialogFlow === 'space'}
        onClose={closeCurrencyDialog}
        onConfirm={handleConfirmCurrencyChange}
        loading={currencyImpactLoading}
        impact={currencyImpact}
        oldCurrencyIso={currencyDialogOldIso}
        newCurrencyIso={currencyDialogNewIso}
      />
      <ConfirmDialog open={!!deleteTarget} target={deleteTarget} onConfirm={handleRevoke} onCancel={() => setDeleteTarget(null)} />
      <DeleteMemberDialog
        open={Boolean(deleteMemberTarget)}
        member={deleteMemberTarget}
        spaceId={activeSpaceId}
        onClose={() => setDeleteMemberTarget(null)}
        onRemovedFromSpace={(userId) => {
          setSpaceMembers((p) => p.filter((m) => m.userId !== userId));
          setDeleteMemberTarget(null);
          showToast(`${deleteMemberTarget?.name} removed from this space.`);
        }}
        onDeletedPermanently={(userId) => {
          setSpaceMembers((p) => p.filter((m) => m.userId !== userId));
          setDeleteMemberTarget(null);
          showToast(`${deleteMemberTarget?.name}'s account has been permanently deleted.`);
        }}
      />
      <SpaceAddMemberDialog open={modal === 'user'} space={resolvedSpace} availableRoles={spaceRoleNames} onClose={() => setModal(null)} onSubmit={handleAddMemberToSpace} />
      <ManageRolesDialog
        open={!!manageRolesMember}
        member={manageRolesMember}
        roles={roles}
        spaceId={activeSpaceId}
        onClose={() => setManageRolesMember(null)}
        onUpdate={() => { refreshMembers(); setManageRolesMember(null); }}
      />
      <UnsavedFlagsDialog
        open={pendingAction !== null}
        saving={savingFlags || configSaving}
        message={spaceTab === 3 ? unsavedCurrencyMessage : undefined}
        onCancel={() => setPendingAction(null)}
        onDiscard={() => {
          if (spaceTab === 3) {
            setSpaceConfig((p) => ({ ...p, currencyId: originalSpaceCurrencyId }));
          } else {
            setSpaceConfig((p) => ({ ...p, ...savedFeatureFlags }));
            setApiVersionInput(spaceConfig?.apiVersion ?? '1.0');
          }
          runPendingAction(pendingAction);
          setPendingAction(null);
        }}
        onSave={async () => {
          if (spaceTab === 3) {
            setPostCurrencySaveAction(pendingAction);
            setPendingAction(null);
            await handleSaveSpaceCurrency();
            return;
          }
          const ok = await handleSaveFeatureSection();
          if (ok) {
            runPendingAction(pendingAction);
            setPendingAction(null);
          }
        }}
      />

      {isSpaceAdmin
        ? <BackBtn label="Back to Home" onClick={() => navigate('/')} />
        : <BackBtn label={`Back to ${currentEntity?.name ?? 'Spaces'}`} onClick={() => {
            if (isActiveTabDirty) { setPendingAction({ type: 'back' }); return; }
            runPendingAction({ type: 'back' });
          }} />
      }

      {/* Space header */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, p: 3, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: C.green }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                  <Box component="span" sx={{ color: '#3b82f6' }}>{resolvedSpace.name}</Box>
                </Typography>
                <Chip label={`Space ID ${resolvedSpace.id}`} size="small"
                  sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: 11, height: 22, fontFamily: 'monospace' }} />
              </Box>
              <Typography variant="caption" sx={{ color: C.muted }}>Manage directory, roles, and member registrations.</Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<PersonAddOutlinedIcon />} onClick={() => setModal('user')} sx={{ ...btnBase, ...ctaNavy, fontSize: 13 }}>
            Add Member
          </Button>
        </Box>

        {/* Quick invite */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2 }}>
          <Box component="form" onSubmit={handleQuickInvite}
            sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 1, bgcolor: '#f8fafc', border: `1px solid ${C.border}`, borderRadius: 2, p: 1.5, flex: 1, flexWrap: { sm: 'wrap' } }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: C.muted, whiteSpace: 'nowrap', mt: { sm: '11px' } }}>Quick invite:</Typography>
            <TextField required size="small" type="email" value={quickEmail} onChange={(e) => setQuickEmail(e.target.value)} placeholder="Email address"
              helperText={quickEmail.trim() === '' ? 'Required' : ''}
              sx={{ flexGrow: 1, minWidth: { sm: 160 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: 13 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
            <FormControl size="small" sx={{ minWidth: { sm: 130 } }}>
              <Select value={quickRole} onChange={(e) => setQuickRole(e.target.value)} sx={{ borderRadius: 1.5, bgcolor: '#fff', fontSize: 13 }}>
                {spaceRoleNames.map((r) => <MenuItem key={r} value={r} sx={{ fontSize: 13 }}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <Button type="submit" variant="contained" startIcon={<SendOutlinedIcon />} size="small" disabled={!isQuickInviteValid}
              sx={{ ...btnBase, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, whiteSpace: 'nowrap', px: 2.5, py: '6.5px' }}>
              Send Invite
            </Button>
          </Box>
          <TextField size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search users…"
            sx={{ minWidth: { sm: 200 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#f8fafc', fontSize: 13, border: `1px solid ${C.border}` } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
        </Box>

        <Tabs value={spaceTab} onChange={(_, v) => {
            if (isActiveTabDirty) { setPendingAction({ type: 'tab', value: v }); return; }
            setSpaceTab(v);
          }}
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 13 }, '& .MuiTabs-indicator': { bgcolor: '#3b82f6' }, borderBottom: `1px solid ${C.border}` }}>
          <Tab value={0} label="👥  Directory & Members" />
          {isGlobalAdmin && <Tab value={1} label="⚙️  Feature Flags" />}
          {(isGlobalAdmin || isEntityAdmin) && <Tab value={2} label="🌐  Languages" />}
          {isGlobalAdmin && <Tab value={3} label="💰  Currency" />}
        </Tabs>
      </Paper>

      {/* ── Directory tab ── */}
      {spaceTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.navy }}>Active Members</Typography>
              <Typography variant="caption" sx={{ color: C.muted }}>{activeMembers.length} user{activeMembers.length !== 1 ? 's' : ''} in this space</Typography>
            </Box>
            {activeMembers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: C.muted }}>
                <PeopleOutlinedIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2" fontWeight={600}>No members found</Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, py: 1.25 } }}>
                    <TableCell>Member</TableCell>
                    <TableCell>Space Role</TableCell>
                    <TableCell>Last Active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeMembers.map((m) => {
                    const isMe = m.email === user?.email;
                    return (
                      <TableRow key={m.userId} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& td': { borderColor: C.border, py: 1.5 } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: C.navy, fontSize: 13, fontWeight: 800, border: `2px solid ${C.border}` }}>
                              {initials(m.name)}
                            </Avatar>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{m.name}</Typography>
                                {isMe && <Chip label="YOU" size="small" sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 800, fontSize: 9, height: 18 }} />}
                              </Box>
                              <Typography variant="caption" sx={{ color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>{m.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(m.roles ?? []).map(r => <RoleChip key={r.roleId} role={formatRole(r.roleName)} />)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: C.muted }}>
                            {m.assignedAt ? new Date(m.assignedAt).toLocaleDateString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Tooltip title="Manage roles">
                              <IconButton size="small" onClick={() => setManageRolesMember(m)}
                                sx={{ color: '#64748b', '&:hover': { bgcolor: '#ede9fe', color: '#7c3aed' } }}>
                                <TuneOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!isMe && (
                              <Tooltip title="Remove from this space">
                                <IconButton size="small" onClick={() => setDeleteMemberTarget({ userId: m.userId, name: m.name })}
                                  sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#fee2e2', color: '#dc2626' } }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </Box>
            )}
          </Paper>

          {pendingInvites.length > 0 && (
            <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.navy }}>Pending Invitations</Typography>
                <Typography variant="caption" sx={{ color: C.muted }}>{pendingInvites.length} invite{pendingInvites.length !== 1 ? 's' : ''} awaiting acceptance</Typography>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, py: 1.25 } }}>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Assigned Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invited</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingInvites.map((inv) => (
                    <TableRow key={inv.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& td': { borderColor: C.border, py: 1.5 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#f1f5f9' }}>
                            <EmailOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontFamily: 'monospace', fontSize: 13 }}>{inv.email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="caption" sx={{ color: C.muted }}>{inv.name || '—'}</Typography></TableCell>
                      <TableCell><RoleChip role={formatRole(inv.roleName)} /></TableCell>
                      <TableCell>
                        <Chip label="Pending" size="small"
                          sx={{ bgcolor: '#fff8e1', color: '#854d0e', fontWeight: 700, fontSize: 10, height: 20 }} />
                      </TableCell>
                      <TableCell><Typography variant="caption" sx={{ color: C.muted }}>{new Date(inv.createdTime).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Typography></TableCell>
                      <TableCell align="right">
                        <Tooltip title="Cancel invitation">
                          <IconButton size="small" onClick={() => setDeleteTarget({ type: 'invite', id: inv.id, label: inv.email })}
                            sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#fee2e2', color: '#dc2626' } }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* ── Feature Flags tab ── */}
      {spaceTab === 1 && isGlobalAdmin && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TuneOutlinedIcon sx={{ fontSize: 18, color: C.muted }} />
              <Typography variant="caption" sx={{ fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Feature Flags</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField required size="small" label="API Version" value={apiVersionInput}
                onChange={(e) => setApiVersionInput(e.target.value)}
                helperText={apiVersionInput.trim() === '' ? 'Required' : ''}
                sx={{ width: { xs: '100%', sm: 130 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <Button variant="contained" size="small" onClick={handleSaveFeatureSection}
                disabled={!spaceConfig || apiVersionInput.trim() === '' || !isFeatureSectionDirty || savingFlags}
                sx={{ ...btnBase, ...ctaGreen }}>
                {savingFlags ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Box>
          {spaceConfig ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              <ToggleRow label="Payments"      checked={!!spaceConfig.payments}      onChange={(v) => handleSpaceConfigToggle('payments', v)} />
              <ToggleRow label="Events"        checked={!!spaceConfig.events}        onChange={(v) => handleSpaceConfigToggle('events', v)} />
              <ToggleRow label="Advertisers"   checked={!!spaceConfig.advertisers}   onChange={(v) => handleSpaceConfigToggle('advertisers', v)} />
              <ToggleRow label="AI Moderation" checked={!!spaceConfig.aiModeration} onChange={(v) => handleSpaceConfigToggle('aiModeration', v)} />
              <ToggleRow label="Selfies"       checked={!!spaceConfig.selfies}       onChange={(v) => handleSpaceConfigToggle('selfies', v)} />
              <ToggleRow label="Lighting"      checked={!!spaceConfig.lighting}      onChange={(v) => handleSpaceConfigToggle('lighting', v)} />
              <ToggleRow label="Items"         checked={!!spaceConfig.items}         onChange={(v) => handleSpaceConfigToggle('items', v)} />
              <ToggleRow label="Rewards"       checked={!!spaceConfig.rewards}       onChange={(v) => handleSpaceConfigToggle('rewards', v)} />
              <ToggleRow label="Charities"     checked={!!spaceConfig.charities}     onChange={(v) => handleSpaceConfigToggle('charities', v)} />
              <ToggleRow label="Subscriptions" checked={!!spaceConfig.subscriptions} onChange={(v) => handleSpaceConfigToggle('subscriptions', v)} />
              <ToggleRow label="Recognition"   checked={!!spaceConfig.recognition}   onChange={(v) => handleSpaceConfigToggle('recognition', v)} />
              <ToggleRow label="Booths"        checked={!!spaceConfig.booths}        onChange={(v) => handleSpaceConfigToggle('booths', v)} />
              <ToggleRow label="Hosting"       checked={!!spaceConfig.hosting}       onChange={(v) => handleSpaceConfigToggle('hosting', v)} />
              <ToggleRow label="Approvals"     checked={!!spaceConfig.approvals}     onChange={(v) => handleSpaceConfigToggle('approvals', v)} />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4, color: C.muted }}>
              <Typography variant="body2">Loading configuration…</Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* ── Languages tab ── */}
      {spaceTab === 2 && (isGlobalAdmin || isEntityAdmin) && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LanguageOutlinedIcon sx={{ fontSize: 18, color: C.muted }} />
            <Typography variant="caption" sx={{ fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Supported Languages
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: C.muted, mb: 3 }}>
            Select the languages available in this space, then click "Set default" on the one that should load first.
          </Typography>

          {languageOptions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: C.muted }}>
              <Typography variant="body2">Loading languages…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mb: 3 }}>
              {languageOptions.map((lang) => {
                const isSelected = spaceLangs.some((l) => l.localeId === lang.localeId);
                const isDefault  = spaceLangs.find((l) => l.localeId === lang.localeId)?.isDefault ?? false;
                return (
                  <Paper key={lang.localeId} elevation={0}
                    onClick={() => handleToggleLang(lang.localeId, lang.displayName)}
                    sx={{
                      border: `1px solid ${isSelected ? '#3b82f6' : C.border}`,
                      borderRadius: 2, p: 1.5,
                      bgcolor: isSelected ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                      '&:hover': { borderColor: '#3b82f6', bgcolor: isSelected ? '#eff6ff' : '#f8fafc' },
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Checkbox checked={isSelected} size="small" sx={{ p: 0 }}
                          onChange={() => handleToggleLang(lang.localeId, lang.displayName)}
                          onClick={(e) => e.stopPropagation()} />
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: isSelected ? '#1e40af' : C.navy }}>
                          {lang.displayName}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <Tooltip title={isDefault ? 'Default language' : 'Set as default'}>
                          <Box component="button" type="button"
                            onClick={(e) => { e.stopPropagation(); handleSetDefaultLang(lang.localeId); }}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
                              border: 'none', cursor: 'pointer', px: 1, py: 0.375, borderRadius: 999,
                              bgcolor: isDefault ? '#fef3c7' : '#f1f5f9',
                              color: isDefault ? '#92400e' : '#475569',
                              fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap',
                              transition: 'background 0.15s, color 0.15s',
                              '&:hover': { bgcolor: isDefault ? '#fde68a' : '#e2e8f0', color: isDefault ? '#78350f' : '#1e293b' },
                            }}>
                            {isDefault
                              ? <RadioButtonCheckedIcon sx={{ fontSize: 16 }} />
                              : <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />}
                            {isDefault ? 'Default' : 'Set default'}
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1, borderTop: `1px solid ${C.border}` }}>
            <Typography variant="caption" sx={{ color: C.muted }}>
              {spaceLangs.length} language{spaceLangs.length !== 1 ? 's' : ''} selected
              {spaceLangs.length > 0 && ` · Default: ${spaceLangs.find((l) => l.isDefault)?.displayName ?? '—'}`}
            </Typography>
            <Button variant="contained" onClick={handleSaveSpaceLangs}
              disabled={langSaving || spaceLangs.length === 0 || !spaceLangs.some((l) => l.isDefault)}
              sx={{ ...btnBase, ...ctaGreen }}>
              {langSaving ? 'Saving…' : 'Save Languages'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── Currency tab ── */}
      {spaceTab === 3 && isGlobalAdmin && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, p: 3, maxWidth: 560 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CreditCardOutlinedIcon sx={{ fontSize: 18, color: C.muted }} />
            <Typography variant="caption" sx={{ fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Space Currency
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: C.muted, mb: 3 }}>
            Choose the currency for this space. The currency tagged "Entity default" is the one set for the whole entity.
            Pick a different one only if this space needs its own.
          </Typography>

          {!spaceConfig ? (
            <Box sx={{ textAlign: 'center', py: 4, color: C.muted }}>
              <Typography variant="body2">Loading configuration…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Currency</InputLabel>
                <Select
                  displayEmpty
                  notched
                  value={spaceConfig.currencyId ?? entityDefaultCurrencyId ?? ''}
                  label="Currency"
                  onChange={(e) => {
                    const picked = e.target.value;
                    // A space with no currency of its own already uses the entity default, so
                    // re-picking it (or reverting to it) must stay "following" rather than pin it.
                    const keepFollowing = originalSpaceCurrencyId == null && picked === entityDefaultCurrencyId;
                    setSpaceConfig((p) => ({ ...p, currencyId: keepFollowing ? null : picked }));
                  }}
                  sx={{ borderRadius: 2 }}>
                  {currencyOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%' }}>
                        <span>{c.ISO} ({c.Symbol}) — {c.Name}</span>
                        {c.id === entityDefaultCurrencyId && (
                          <Chip label="Entity default" size="small" variant="outlined"
                            sx={{ height: 20, fontSize: 11, fontWeight: 600, color: C.muted, borderColor: C.border }} />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {spaceCurrencyStatus && (
                  <FormHelperText data-testid="space-currency-entity-default">{spaceCurrencyStatus}</FormHelperText>
                )}
              </FormControl>
              <Button variant="contained" onClick={handleSaveSpaceCurrency} disabled={configSaving || currencyImpactLoading || !isCurrencyDirty}
                sx={{ ...btnBase, ...ctaGreen, alignSelf: 'flex-start' }}>
                {configSaving ? 'Saving…' : 'Save Currency'}
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // LEVEL 4 — Entity Configuration
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'entity-config' && currentEntity && isGlobalAdmin) {
    const color = entityColor(entities.findIndex((e) => e.id === activeEntityId));
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Toaster />
        <CurrencyChangeConfirmDialog
          open={currencyDialogOpen && currencyDialogFlow === 'entity'}
          onClose={closeCurrencyDialog}
          onConfirm={handleConfirmCurrencyChange}
          loading={currencyImpactLoading}
          impact={currencyImpact}
          oldCurrencyIso={currencyDialogOldIso}
          newCurrencyIso={currencyDialogNewIso}
        />
        <BackBtn label={`Back to ${currentEntity.name}`} onClick={() => setView('spaces')} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar sx={{ width: 44, height: 44, bgcolor: color, fontSize: 16, fontWeight: 800 }}>{initials(currentEntity.name)}</Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: C.navy, lineHeight: 1 }}>Entity Configuration</Typography>
            <Typography variant="body2" sx={{ color: C.muted }}>{currentEntity.name} — default currency applied across all spaces</Typography>
          </Box>
        </Box>

        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${C.border}`, p: 3, maxWidth: 560 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <CreditCardOutlinedIcon sx={{ fontSize: 18, color: C.muted }} />
            <Typography variant="caption" sx={{ fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Default Currency</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Default Currency</InputLabel>
              <Select value={entityConfig.currencyId ?? ''} label="Default Currency" onChange={(e) => setEntityConfig((p) => ({ ...p, currencyId: e.target.value }))} sx={{ borderRadius: 2 }}>
                {currencyOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.ISO} ({c.Symbol}) — {c.Name}</MenuItem>
                ))}
              </Select>
              {!entityConfig.currencyId && <FormHelperText>Required</FormHelperText>}
            </FormControl>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Contract Percentage"
              value={entityConfig.contractPercentage ?? 0}
              onChange={(e) => setEntityConfig((p) => ({ ...p, contractPercentage: e.target.value }))}
              helperText="Applied to the gateway fee on every transaction across all spaces under this entity, for reconciliation."
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              InputProps={{ endAdornment: <Typography fontSize={13} sx={{ color: C.muted }}>%</Typography> }}
            />
            <Button variant="contained" onClick={handleSaveEntityConfig} disabled={configSaving || currencyImpactLoading || !entityConfig.currencyId}
              sx={{ ...btnBase, ...ctaGreen, alignSelf: 'flex-end' }}>
              {configSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return null;
}
